/** Fall progress dial: rotation math (HTML/CSS disc in left chrome). */

export const DIAL_REST_DEG = 0;
export const DIAL_DAWN_DEG = 180;
export const DIAL_PLAY_SWEEP_DEG = 180;
export const DIAL_CLOCK_SEGMENT_COUNT = 12;
export const DIAL_TICK_STEP_DEG = 360 / DIAL_CLOCK_SEGMENT_COUNT;
/** Equator marks sit on the light/dark seam — omit for a cleaner split. */
export const DIAL_TICK_SKIP_DEGS = [90, 270];

/**
 * Clock-face tick angles (12 × 30°, minus equator skips).
 * @returns {number[]}
 */
export function getDialTickDegrees() {
  var degs = [];
  for (var i = 0; i < DIAL_CLOCK_SEGMENT_COUNT; i++) {
    var deg = i * DIAL_TICK_STEP_DEG;
    if (DIAL_TICK_SKIP_DEGS.indexOf(deg) >= 0) continue;
    degs.push(deg);
  }
  return degs;
}

/**
 * Tick contrast on the disc hemispheres (dark top / light bottom at rest pose).
 * @param {number} deg clockwise from 12 o'clock
 * @returns {"lit" | "ink"}
 */
export function getDialTickTone(deg) {
  var d = ((Number(deg) % 360) + 360) % 360;
  return d > 270 || d < 90 ? "lit" : "ink";
}

/**
 * @param {number} t
 * @returns {number}
 */
export function clamp01(t) {
  var n = Number(t);
  if (!isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/**
 * @param {number} t 0..1
 * @returns {number} 0..1
 */
export function easeInOut(t) {
  t = clamp01(t);
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Merge→Fall bridge: 0° → 180° with ease-in-out.
 * @param {number} t 0..1 elapsed fraction
 * @returns {number}
 */
export function bridgeProgressToDialDeg(t) {
  return easeInOut(t) * DIAL_DAWN_DEG;
}

/**
 * Fall active play after countdown: 180° → 360° (≡ rest). Settle holds at rest.
 * @param {number} progress from computeFallBarShrink
 * @returns {number}
 */
export function fallProgressToDialDeg(progress) {
  return DIAL_DAWN_DEG + clamp01(progress) * DIAL_PLAY_SWEEP_DEG;
}

/**
 * @param {number} cycleIndex
 * @returns {number}
 */
export function getDialDayNumber(cycleIndex) {
  return Math.max(1, Math.floor(Number(cycleIndex) || 0) + 1);
}

/**
 * @param {{
 *   stateMachine?: { is?: (s: string) => boolean },
 *   celestialTransition?: { direction?: string, durationMs?: number, startedAt?: number } | null,
 *   runPhase?: object | null,
 *   currentCycleIndex?: number,
 *   isSimPaused?: boolean,
 *   bridgeCelestialPauseWallMs?: number | null,
 *   buildFallSnap?: (now: number) => object | null,
 *   computeShrink?: (fall: object, cfg: object) => number,
 *   runConfig?: object,
 *   nowMs?: number,
 * }} ctx
 * @returns {{ deg: number, day: number }}
 */
export function resolveFallProgressDial(ctx) {
  ctx = ctx || {};
  var day = getDialDayNumber(ctx.currentCycleIndex);
  var sm = ctx.stateMachine;
  var is = sm && typeof sm.is === "function" ? sm.is.bind(sm) : function () { return false; };
  var now = ctx.nowMs != null && isFinite(ctx.nowMs)
    ? ctx.nowMs
    : 0;

  if (is("transition") && ctx.celestialTransition
    && ctx.celestialTransition.direction === "to_fall") {
    var tr = ctx.celestialTransition;
    var duration = Math.max(1, Number(tr.durationMs) || 1);
    var startedAt = Number(tr.startedAt) || 0;
    var elapsed = now - startedAt;
    if (ctx.isSimPaused && ctx.bridgeCelestialPauseWallMs != null
      && isFinite(ctx.bridgeCelestialPauseWallMs)) {
      elapsed = ctx.bridgeCelestialPauseWallMs - startedAt;
    }
    return {
      deg: bridgeProgressToDialDeg(elapsed / duration),
      day: day,
    };
  }

  if (is("fall") && ctx.runPhase && typeof ctx.buildFallSnap === "function") {
    var snap = ctx.buildFallSnap(now);
    if (snap && snap.state === "countdown") {
      return { deg: DIAL_DAWN_DEG, day: day };
    }
    var shrink = typeof ctx.computeShrink === "function"
      ? ctx.computeShrink(snap || {}, ctx.runConfig || {})
      : 0;
    return { deg: fallProgressToDialDeg(shrink), day: day };
  }

  return { deg: DIAL_REST_DEG, day: day };
}

/**
 * @param {number} deg
 * @returns {number} 0..360
 */
export function normalizeDialDeg(deg) {
  var d = Number(deg);
  if (!isFinite(d)) return 0;
  return ((d % 360) + 360) % 360;
}

/** Fixed top label sits over the light slice (countdown + mid-bridge/play). */
export function isDialTopLight(deg) {
  var d = normalizeDialDeg(deg);
  return d > 90 && d < 270;
}

/** Fixed bottom caption sits over the light slice (merge rest + fall end). */
export function isDialBottomLight(deg) {
  var d = normalizeDialDeg(deg);
  return d <= 90 || d >= 270;
}

/** @deprecated Use isDialTopLight */
export function isDialLightDome(deg) {
  return isDialTopLight(deg);
}

/**
 * @param {number} deg
 * @returns {string}
 */
export function formatDialRotCssVar(deg) {
  return normalizeDialDeg(deg) + "deg";
}

/**
 * @param {number} deg
 * @returns {string}
 */
export function formatDialDiscTransform(deg) {
  var d = Number(deg);
  if (!isFinite(d)) d = 0;
  d = ((d % 360) + 360) % 360;
  if (d === 0) return "rotate(0deg)";
  return "rotate(" + d.toFixed(3) + "deg)";
}